import { Modal } from "@stormwater-software/lib-ui";
import { capitalize, findIndex } from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import AppDivisionContext from "../../contexts/AppDivisionContext";
import RolesContext from "../../contexts/RolesContext";
import { getDivisionResourceName } from "../../hooks/divisionResource";
import userApi from "../../utils/api/user";
import { rolesApi } from "../../utils/api/roles";
import ConfirmationModal from "../Shared/ConfirmationModal/ConfirmationModal";
import ResourceIndex from "../Shared/ResourceIndex/ResourceIndex";
import AddUserModal from "./AddUser/AddUserModal";
import UserDetail from "./Detail";
import Loading from "../Shared/ResourceIndex/Loading";
import { Redirect } from "react-router-dom";
import DashboardContext from "../../contexts/DashboardContext";

import UserStatus, { USER_STATUSES } from "./Index/UserStatus";
import ToastError from "../Shared/ToastError/ToastError";
import { toast } from "react-toastify";

import { useDisplayName } from "../../hooks/useTerminology";
/** title and subtitle to toggle activation on a user */
const getActivationModalContent = (status, name) => {
  const action = status === USER_STATUSES.active ? "de-activate" : "activate";

  return {
    title: `${capitalize(action)} User`,
    subTitle: [`Are you sure you want to ${action} ${name}?`],
  };
};

const resourceName = getDivisionResourceName("users"); // for api data
const pageName = "Users"; // functional area for permissions

const Users = () => {
  const appDivisionContext = useContext(AppDivisionContext);
  const queryClient = useQueryClient();
  const permCheck = useContext(RolesContext).userHasPermission;
  const displayName = useDisplayName();
  const canCreate = permCheck("create", pageName);
  const { getTeamManagementData } = useContext(DashboardContext)
  /** keep track of invitation modal */
  const [showInvite, setShowInvite] = useState(false);
  const [showActivation, setShowActivation] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [projectList, setProjectList] = useState([])

  /** keep track selected user */
  const [selectedId, setSelectedId] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState();
  /** SW2-2508: the logged-in user, and the roles they may assign (= the roles whose
   * holders they may edit). Drives edit-form vs read-only below. */
  const [currentUserId, setCurrentUserId] = useState(null)
  const [assignableRoleNames, setAssignableRoleNames] = useState([])
  /** Until this is true the authorization data is still loading; the empty initial
   * set must not be treated as authoritative for the edit/read-only decision. */
  const [authzLoaded, setAuthzLoaded] = useState(false)
  /**
   * keep track of user ids that are "busy"
   */
  const [showSpinner, setShowSpinner] = useState([]);
  const hideSpinner = useCallback((id, idList) => {
    // find index inviteId in showSpinner
    const idIndex = findIndex(idList, id);

    setShowSpinner([...idList.slice(0, idIndex), ...idList.slice(idIndex + 1)]);
  }, []);

  const selectUser = useCallback(user => {
    setSelectedId(user.id);
    setSelectedName(user.name);
    setSelectedStatus(user.status);
  });

  const deselect = useCallback(() => {
    setSelectedId(null);
    setSelectedName(null);
    setSelectedStatus(null);
  });

  useEffect(() => {
    userApi
      .profile()
      .then((res) => {
        setCurrentUserId(res.id)
        // SW2-2508: the roles this caller may assign are exactly the roles whose holders
        // they may edit. getRolesByTenant is filtered to the caller's reach by SW2-2508;
        // the :tenantId path param is ignored server-side (the caller's own tenant decides),
        // so the value passed here is immaterial.
        return rolesApi
          .getRolesByTenant(res.tenantId)
          .then((roles) => setAssignableRoleNames(roles.map((r) => r.name)))
          .catch(() => setAssignableRoleNames([]))
      })
      // Mark loaded only once identity + assignable roles have settled, so a row
      // clicked during the fetch is not decided against the empty initial set.
      .finally(() => setAuthzLoaded(true))
  },[])

  return (
    <div className="users-index">
      <ResourceIndex
        resourceName={resourceName}
        resourceQueryProps={{ divisionId: appDivisionContext.appDivisionId }}
        resourceTableHeaders={[
          ["First Name", "first_name"],
          ["Last Name", "last_name"],
          ["Email", "email"],
          ["Role", "roleName"],
          ["Status", "status"],
        ]}
        resourceSearchProps={["first_name", "last_name", "email"]}
        enablePagination
        enableRead={permCheck("read", pageName)}
        enableEdit={permCheck("update", pageName)}
        enableCallToAction={canCreate}
        searchInputPlaceholder="Search Users"
        callToActionContent="Add Users"
        renderReader={(user, handleClose) => (
            <Modal
              title="User Profile"
              handleClose={handleClose}
              handleSubmit={handleClose}
              dismissOnEsc={true}
              dismissOnBgClick={true}
              show={true}
            >
              <UserDetail {...user} />
            </Modal>
        )}

        renderEditor={(user, handleClose) => {
          // SW2-2508: defer the decision until the authorization data has loaded —
          // otherwise a row clicked during the fetch would be judged against the empty
          // initial set and an editable user (or self) would wrongly render as read-only.
          // The modal re-renders into the editor / read-only view once loaded.
          if (!authzLoaded) {
            return (
              <Modal
                title="User Profile"
                handleClose={handleClose}
                handleSubmit={handleClose}
                dismissOnEsc={true}
                dismissOnBgClick={true}
                show={true}
              >
                <Loading what="user" />
              </Modal>
            )
          }

          // SW2-2508: open the editable form only for users this caller may edit — the
          // ones whose role is within their assignable set, or themselves. Everyone else
          // gets the read-only modal, matching the backend edit-gate (SW2-2508) so the
          // user never reaches a save that would 403.
          const isEditable =
            user.id === currentUserId ||
            assignableRoleNames.includes(user.roleName)

          if (isEditable) {
            return (
              <Redirect
                push
                to={`/divisions/${appDivisionContext.appDivisionId}/user-profiles/${user.id}/edit`}
              />
            )
          }

          return (
            <Modal
              title="User Profile"
              handleClose={handleClose}
              handleSubmit={handleClose}
              dismissOnEsc={true}
              dismissOnBgClick={true}
              show={true}
            >
              <UserDetail {...user} />
            </Modal>
          )
        }}
        renderCallToAction={(handleClose, pushData) => (
          <AddUserModal
            onClose={handleClose}
            onSubmit={newUsers => {
              pushData(...newUsers);
              handleClose();
            }}
          />
        )}
        renderRowCell={(user, propName) => {
          switch (propName) {
            // SW2-2366: shown in the tenant's wording; user.roleName itself is
            // still what restrictEdit compares.
            case "roleName":
              return displayName(user.roleName);
            case "status":
              return (
                <UserStatus
                  user={user}
                  isBusy={showSpinner.includes(user.id)}
                  onInvite={() => {
                    if (permCheck("create", pageName)) {
                      selectUser(user);
                      setShowInvite(true);
                    }
                  }}
                  onToggleActivation={nextStatus => {
                    if (permCheck("update", pageName)) {
                      selectUser(user);
                      setShowActivation(true);
                    }
                  }}
                  canInvite={permCheck("create", pageName)}
                  canToggleActivation={permCheck("update", pageName)}
                />
              );
            default:
              return ResourceIndex.defaultProps.renderRowCell(user, propName);
          }
        }}
      />
      {
        /** invitation modal */
        <ConfirmationModal
          show={showInvite}
          title="Re-Invite User"
          subTitle={[
            `Are you sure you want to re-invite ${selectedName}?`,
            "Their previous invitations will no longer be valid.",
          ]}
          buttonText="Confirm"
          handleClose={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowInvite(false);
            deselect();
          }}
          handleConfirm={() => {
            setShowSpinner([...showSpinner, selectedId]);
            setShowInvite(false);

            return userApi.invite(selectedId).then(() => {
              hideSpinner(selectedId, showSpinner);
              deselect();
            });
          }}
        />
      }
      {
        /** active/inactive modal */
        <ConfirmationModal
          show={showActivation}
          {...getActivationModalContent(selectedStatus, selectedName)}
          buttonText="Confirm"
          handleClose={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowActivation(false);
            deselect();
          }}
          handleConfirm={async () => {
            setShowSpinner([...showSpinner, selectedId]);
            setShowActivation(false);
            
            const getProjectList = await userApi.getProjectList(selectedId)
            
            if(getProjectList.length === 0 || selectedStatus === USER_STATUSES.inactive) {
              const toastId = toast("Updating Status");

              return userApi
                .toggleActivation(selectedId)
                .then(async ({ status }) => {
                  const query = queryClient
                    .getQueryCache()
                    .getAll()
                    .filter(q => q.queryKey && q.queryKey.includes("divisionUsers_"));

                  if (query.length) {
                    query.map(({ queryKey }) =>
                      queryClient.invalidateQueries(queryKey),
                    );
                  }
                  
                  toast.update(toastId, {
                    render: 'Updated Status',
                    type: 'success',
                  });
          
                  hideSpinner(selectedId, showSpinner);
                  getTeamManagementData.refetch()
                  deselect();
                }).catch((error) => {

                  toast.update(toastId,{
                    render: (
                      <ToastError
                        message="Cannot Update Status"
                        error={error.response.data.message}
                      />
                    ),
                    autoClose: false,
                  });
                  hideSpinner(selectedId, showSpinner);
                  deselect();
                });
            } else {
              setShowWarning(true)
              setProjectList(getProjectList)
              hideSpinner(selectedId, showSpinner);
              deselect();
            }
          }}
        />
      }
      {
        /** warning modal */
        <Modal
          show={showWarning}
          title="Warning"
          subTitle={[
            `This user is already assigned as the inspector for following active projects.`,
          ]}
          submitBtnText="Close"
          handleClose={() => {
            setShowWarning(false);
            setProjectList([])
          }}
          handleSubmit={() => {
            setShowWarning(false);
            setProjectList([])
          }}
        >
          <div>
            <ol>
              {projectList.map((project) => <li>{project.name}</li>)}
            </ol>
            <p 
              className="col-danger"
              style={{
                fontStyle: "italic",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >Assign new inspector to these projects prior deactivating this user</p>
          </div>
        </Modal>
      }
    </div>
  );
};

export default Users;
